// The sign-in card: the whole of what the drive shows someone it has no session for, and the
// same card the callback route turns through as the round trip finishes. One button and no
// password field — the drive never collects a credential itself, and the button names the
// provider the person is being sent to.
//
// The authorization URL is asked for on the press rather than on mount. The trip leaves from this
// tab, so there is no popup to outlive and nothing to gain by asking early, and a URL asked for on
// mount is state the server holds open for every visit that never presses the button.
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {useMutation} from '@apollo/client/react'
import {useState} from 'react'
import {v4 as uuid} from 'uuid'

import CheckIcon from '@mui/icons-material/CheckRounded'
import CircularProgress from '@mui/material/CircularProgress'
import CloseIcon from '@mui/icons-material/CloseRounded'
import ShieldIcon from '@mui/icons-material/ShieldOutlined'

import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {OAUTH_CODE_REQUEST} from '../../graphql/mutations/oauth'
import {appUrl, openAuthorization} from '../../lib/locationTools'

import './accountsSignIn.scss'

const CALLBACK = '/auth/sign-in'
const SCOPE = 'api:read api:write profile'

const spinner = <CircularProgress size={14} thickness={5} aria-hidden='true' />

const STATUSES = {
  asking: {signal: spinner, copy: 'Requesting authorization code…'},
  returning: {signal: spinner, copy: 'Finishing up with Accounts…'},
  granted: {signal: <CheckIcon fontSize='small' />, copy: 'Authorized. Opening your drive…', tone: 'ok'},
  refused: {signal: <CloseIcon fontSize='small' />, copy: 'Authorization was refused. Try again.', tone: 'failed'},
  unreachable: {signal: <CloseIcon fontSize='small' />, copy: 'Accounts didn’t answer. Try again.', tone: 'failed'}
}

// The phases where pressing again is the way forward. Anywhere else a trip is already under way,
// and a second one would race it to the same callback.
const PRESSABLE = [null, 'refused', 'unreachable']

const AccountsSignIn = ({phase = null}) => {
  const [requestOAuthCode] = useMutation(OAUTH_CODE_REQUEST)
  // This card's own part of the trip, which outranks the phase it was handed: a press after a
  // refusal is a new trip, and the card says so rather than repeating how the last one ended.
  const [trip, setTrip] = useState(null)
  const current = trip || phase
  const status = STATUSES[current]

  const handleClick = () => {
    setTrip('asking')

    requestOAuthCode({
      variables: {
        input: {
          state: uuid(),
          scope: SCOPE,
          redirectUri: encodeURIComponent(appUrl(CALLBACK))
        }
      },
      // The card stays on "requesting" as the tab leaves for Accounts. Turning it back would only
      // flash an idle button at someone who is already on their way.
      onCompleted: ({oauthAuthorize}) => (
        oauthAuthorize?.uri ? openAuthorization(oauthAuthorize.uri) : setTrip('unreachable')
      ),
      onError: () => setTrip('unreachable')
    })
  }

  return (
    <div className='accounts-sign-in'>
      <div className='accounts-card flex-center'>
        <img className='accounts-mark' src='/drive.svg' alt='' />
        <h1 className='accounts-title'>Drive</h1>
        <Text className='accounts-copy'>Your files, encrypted before they leave this browser.</Text>
        <BaseButton className='accounts-button' disabled={!PRESSABLE.includes(current)} onClick={handleClick}>
          <ShieldIcon fontSize='small' />Continue with Accounts
        </BaseButton>
        {/* Live, because the card turns on its own as the round trip finishes. */}
        <Text className={classnames('accounts-status', status?.tone)} role='status'>
          {status && <>{status.signal}{status.copy}</>}
        </Text>
        <Text className='accounts-legal'>
          Drive never sees your password. Accounts issues the token, and you can withdraw it there at any time.
        </Text>
      </div>
    </div>
  )
}

AccountsSignIn.propTypes = {
  phase: PropTypes.oneOf(['returning', 'granted', 'refused', null])
}

export default AccountsSignIn
